import { useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import { formatDateCST, formatTimeCST } from '../../../../utlis';

// Status options
const statusOptions = [
  { value: 'Active', text: 'Active' },
  { value: 'Inactive', text: 'Inactive' },
];

// Phone formatting helper
const formatPhoneInput = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }
};

const FacultyModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  isSubmitting,
}) => {
  const phoneInputRef = useRef(null);
  const [initialValues, setInitialValues] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      totalEnrollment: '',
      status: 'Active',
      misc: '',
    },
  });

  // Watch all form fields for changes
  const watchedFields = watch();
  const phoneValue = watch('phone');

  // Check if form has any changes compared to initial values
  const hasChanges = () => {
    if (!initialValues) return !isEditMode; // For new faculty, always enable save

    const fieldsToCompare = ['firstName', 'lastName', 'email', 'phone', 'totalEnrollment', 'status', 'misc'];

    return fieldsToCompare.some((field) => {
      const currentValue = String(watchedFields[field] || '').trim();
      const initialValue = String(initialValues[field] || '').trim();
      return currentValue !== initialValue;
    });
  };

  // Handle phone input formatting
  const handlePhoneChange = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const previousValue = phoneValue || '';
    const newValue = e.target.value;

    // Get only digits from input
    const digits = newValue.replace(/\D/g, '').slice(0, 10);
    const formatted = formatPhoneInput(digits);

    setValue('phone', formatted);

    // Adjust cursor position after formatting
    setTimeout(() => {
      if (phoneInputRef.current) {
        const digitsBefore = previousValue.slice(0, cursorPosition).replace(/\D/g, '').length;
        let newCursorPos = 0;
        let digitCount = 0;

        for (let i = 0; i < formatted.length && digitCount < digitsBefore; i++) {
          newCursorPos = i + 1;
          if (/\d/.test(formatted[i])) {
            digitCount++;
          }
        }

        // If we're adding characters, move cursor forward
        if (newValue.length > previousValue.length) {
          newCursorPos = Math.min(newCursorPos + 1, formatted.length);
        }

        phoneInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      // Format phone for display
      const formattedPhone = defaultValues.phone
        ? formatPhoneInput(String(defaultValues.phone).replace(/\D/g, ''))
        : '';

      const formValues = {
        ...defaultValues,
        phone: formattedPhone,
      };

      reset(formValues);

      // Store initial values for change detection
      setInitialValues({
        firstName: formValues.firstName || '',
        lastName: formValues.lastName || '',
        email: formValues.email || '',
        phone: formValues.phone || '',
        totalEnrollment: formValues.totalEnrollment || '',
        status: formValues.status || 'Active',
        misc: formValues.misc || '',
      });
    }
  }, [isOpen, defaultValues, reset]);

  const handleClose = () => {
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      totalEnrollment: '',
      status: 'Active',
      misc: '',
    });
    setInitialValues(null);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[900px] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Modal Title and Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode ? 'Edit Faculty' : 'Add New Faculty'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form
            className="flex flex-col gap-4 md:gap-6 text-text-hint"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Row 1: First Name, Last Name, Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="firstName" className="text-text-hint mb-1">
                  First Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('firstName', {
                    required: 'First Name is required',
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="First Name"
                  id="firstName"
                  className={`outline-none border-2 rounded-lg px-3 py-2 text-black ${
                    errors.firstName ? 'border-danger-500' : 'border-secondary-500'
                  }`}
                />
                {errors.firstName && (
                  <span className="text-red-500 text-xs">{errors.firstName.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="lastName" className="text-text-hint mb-1">
                  Last Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('lastName', {
                    required: 'Last Name is required',
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Last Name"
                  id="lastName"
                  className={`outline-none border-2 rounded-lg px-3 py-2 text-black ${
                    errors.lastName ? 'border-danger-500' : 'border-secondary-500'
                  }`}
                />
                {errors.lastName && (
                  <span className="text-red-500 text-xs">{errors.lastName.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="email" className="text-text-hint mb-1">
                  Email <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    maxLength: { value: 50, message: 'Max 50 characters' },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  placeholder="Email"
                  id="email"
                  className={`outline-none border-2 rounded-lg px-3 py-2 text-black ${
                    errors.email ? 'border-danger-500' : 'border-secondary-500'
                  }`}
                />
                {errors.email && (
                  <span className="text-red-500 text-xs">{errors.email.message}</span>
                )}
              </div>
            </div>

            {/* Row 2: Phone, Total Enrollment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="phone" className="text-text-hint mb-1">
                  Phone <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('phone', {
                    required: 'Phone is required',
                    maxLength: { value: 25, message: 'Max 25 characters' },
                  })}
                  ref={(e) => {
                    register('phone').ref(e);
                    phoneInputRef.current = e;
                  }}
                  type="text"
                  placeholder="(999) 999 9999"
                  id="phone"
                  onChange={handlePhoneChange}
                  className={`outline-none border-2 rounded-lg px-3 py-2 text-black ${
                    errors.phone ? 'border-danger-500' : 'border-secondary-500'
                  }`}
                />
                {errors.phone && (
                  <span className="text-red-500 text-xs">{errors.phone.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="totalEnrollment" className="text-text-hint mb-1">
                  Total Enrollment
                </label>
                <input
                  {...register('totalEnrollment', {
                    min: { value: 0, message: 'Must be 0 or greater' },
                  })}
                  type="number"
                  placeholder="Total Enrollment"
                  id="totalEnrollment"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.totalEnrollment && (
                  <span className="text-red-500 text-xs">{errors.totalEnrollment.message}</span>
                )}
              </div>
            </div>

            {/* Row 3: Notes */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="misc" className="text-text-hint mb-1">
                Notes
              </label>
              <textarea
                {...register('misc')}
                placeholder="Additional notes or miscellaneous information"
                id="misc"
                rows={3}
                className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black resize-none"
              />
            </div>

            {/* Row 4: Status */}
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint mb-1">
                Status <span className="text-danger-500">*</span>
              </label>
              <ul className="grid grid-cols-2 w-64">
                {statusOptions.map((option, index) => (
                  <li key={option.value} className="relative">
                    <input
                      {...register('status', { required: 'Status is required' })}
                      className="sr-only peer"
                      type="radio"
                      value={option.value}
                      id={`status-${option.value}`}
                    />
                    <label
                      className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 ${
                        index === 0
                          ? 'rounded-tl-full rounded-bl-full'
                          : 'rounded-tr-full rounded-br-full'
                      }`}
                      htmlFor={`status-${option.value}`}
                    >
                      {option.text}
                    </label>
                  </li>
                ))}
              </ul>
              {errors.status && (
                <span className="text-red-500 text-xs">{errors.status.message}</span>
              )}
            </div>

            {/* Display-only fields for Edit Mode */}
            {isEditMode && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Created By</label>
                  <p className="text-sm text-gray-600">{defaultValues?.created_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Date Created</label>
                  <p className="text-sm text-gray-600">
                    {defaultValues?.created ? formatDateCST(defaultValues.created) : 'N/A'}
                  </p>
                  {defaultValues?.created && (
                    <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.created)}</p>
                  )}
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Updated By</label>
                  <p className="text-sm text-gray-600">{defaultValues?.updated_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Last Updated</label>
                  <p className="text-sm text-gray-600">
                    {defaultValues?.updated ? formatDateCST(defaultValues.updated) : 'N/A'}
                  </p>
                  {defaultValues?.updated && (
                    <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.updated)}</p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                <button
                  type="submit"
                  disabled={isSubmitting || !hasChanges()}
                  className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default FacultyModal;
